import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';

import { ArrowLeft } from 'lucide-react';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  useConfirmDialog,
} from '@/components/ui';
import { useActivityType } from '@/lib/activity-types-store';
import {
  useTaskTypeAutomations,
  useTaskTypeAutomationActions,
  type CreateTaskTypeAutomationInput,
  type TaskTypeAction,
  type TaskTypeAutomation,
  type TaskTypeTrigger,
} from '@/lib/task-type-automations-store';
import { cn } from '@/lib/cn';
// BUG-V2-058 / Sprint 3 (2026-05-25): substitui dialog interno duplicado pelo
// shell shared. Dual-backend OK — shell dispatcha pra TaskTypeAutomationForm
// que chama /api/v1/task-type-automations.
import { AutomationFormDialog } from '@/components/automations/AutomationFormDialog';

import { formatDataHoraSegundos } from '@/lib/format-date';
/**
 * /admin/tipos-atividade/:id/automacoes — UI pra automações de UM tipo
 * (R5.2 Bloco L).
 *
 * Lista todas as automations do tipo + permite create/edit/delete + sandbox
 * dispatch manual pra testar configuração. Histórico de runs via lista
 * embedded por automation card.
 */

const TRIGGER_LABELS: Record<TaskTypeTrigger, string> = {
  on_create: 'Quando a atividade é criada',
  on_complete: 'Quando a atividade é concluída',
  on_overdue: 'Quando a atividade fica atrasada',
  on_assigned: 'Quando alguém vira responsável',
};

// FASE-C (copy): "(stub)" era jargão de dev. Como estas ações realmente ainda
// não disparam, a UI diz isso em português — e o AcoesEditor não as oferece
// mais em automações novas.
const ACTION_LABELS: Record<TaskTypeAction['type'], string> = {
  notify_in_app: 'Avisar no app',
  send_whatsapp: 'Enviar WhatsApp',
  send_email: 'Enviar e-mail',
};

const ACTION_EM_PREPARACAO: ReadonlyArray<TaskTypeAction['type']> = [
  'send_whatsapp',
  'send_email',
];

const ALVO_LABELS: Record<string, string> = {
  responsavel: 'responsável',
  criador: 'quem criou',
  cliente: 'cliente',
  todos_no_card: 'todos no card',
  user_specific: 'uma pessoa específica',
  round_robin: 'rodízio entre as pessoas',
  lista: 'lista de pessoas',
};

export function TipoAtividadeAutomacoesPage() {
  // FASE 2 / M1: ConfirmDialog (substitui window.confirm)
  const confirm = useConfirmDialog();
  const { id } = useParams<{ id: string }>();
  const { data: tipo, isLoading: loadingTipo } = useActivityType(id ?? null);
  const { data: automations = [], isLoading: loadingAutos } = useTaskTypeAutomations(id ?? null);
  const { create, update, remove, dispatch, pending } = useTaskTypeAutomationActions();

  const [editing, setEditing] = useState<TaskTypeAutomation | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loadingTipo) {
    return (
      <p className="text-sm text-ink-3 dark:text-ink-dark-3 italic py-8 text-center">
        Carregando…
      </p>
    );
  }
  if (!tipo) {
    return (
      <Alert variant="danger" rail>
        Este tipo de atividade não existe mais.{' '}
        <Link to="/admin/tipos-atividade" className="underline">
          Voltar pra lista de tipos
        </Link>
      </Alert>
    );
  }

  async function handleSave(input: CreateTaskTypeAutomationInput) {
    setError(null);
    try {
      if (editing) {
        await update(editing.id, input);
      } else {
        await create(input);
      }
      setEditing(null);
      setCreating(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar a automação.');
    }
  }

  async function handleDelete(a: TaskTypeAutomation) {
    const ok = await confirm({
      title: 'Excluir automação',
      message: `Excluir a automação "${a.nome}"? O histórico de execuções dela some junto.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await remove(a.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível excluir a automação.');
    }
  }

  async function handleDispatch(a: TaskTypeAutomation) {
    // FASE-C (copy): "targetId pra simular" / "runs criadas" eram jargão cru.
    const targetId =
      window.prompt(
        'Digite o identificador do item que vai receber o teste:',
        'teste',
      ) ?? '';
    if (!targetId) return;
    try {
      const r = await dispatch(a.id, { targetId });
      window.alert(
        `Teste enviado. ${r.runsCreated} ${r.runsCreated === 1 ? 'execução registrada' : 'execuções registradas'}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível rodar o teste.');
    }
  }

  return (
    <div className="min-w-0 space-y-4" data-testid="tipo-automacoes-page">
      <PageHeader
        crumb="Configurações · Tipos de atividade"
        title={`Automações de ${tipo.label}`}
        subtitle={`O que o sistema faz sozinho quando uma atividade do tipo “${tipo.label}” é criada, concluída, atrasa ou ganha responsável.`}
        actions={
          <>
            <Link
              to="/admin/tipos-atividade"
              className="inline-flex items-center gap-1 text-sm font-medium text-ink-2 dark:text-ink-dark-2 hover:text-red dark:hover:text-red-glow transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Tipos de atividade
            </Link>
            <Button
              type="button"
              data-testid="tipo-automacao-new"
              onClick={() => setCreating(true)}
            >
              + Nova automação
            </Button>
          </>
        }
      />

      {error && (
        <Alert variant="danger" rail>
          {error}
        </Alert>
      )}

      {loadingAutos ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 italic">
          Carregando automações…
        </p>
      ) : automations.length === 0 ? (
        <Card variant="flat" className="min-w-0">
          <EmptyState
            title="Nenhuma automação configurada"
            description="Crie a primeira pra o sistema agir sozinho quando uma atividade deste tipo acontecer."
          />
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="tipo-automacoes-list">
          {automations.map((a) => (
            <AutomationCard
              key={a.id}
              automation={a}
              onEdit={() => setEditing(a)}
              onDelete={() => handleDelete(a)}
              onDispatch={() => handleDispatch(a)}
              busy={pending === a.id}
            />
          ))}
        </ul>
      )}

      {(creating || editing) && (
        <AutomationFormDialog
          open
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          contexto={{ kind: 'task_type', activityTypeId: tipo.id }}
          onSubmitTaskType={handleSave}
          editingTaskType={editing}
          taskTypeBusy={
            pending === 'create' || (editing && pending === editing.id) || false
          }
        />
      )}
    </div>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────

function AutomationCard({
  automation,
  onEdit,
  onDelete,
  onDispatch,
  busy,
}: {
  automation: TaskTypeAutomation;
  onEdit: () => void;
  onDelete: () => void;
  onDispatch: () => void;
  busy: boolean;
}) {
  return (
    <li
      data-testid={`tta-card-${automation.id}`}
      className={cn(
        'p-3 rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface',
        !automation.enabled && 'opacity-60'
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 truncate">
            {automation.nome}
          </p>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5 italic">
            {TRIGGER_LABELS[automation.trigger]}
          </p>
        </div>
        <Badge variant={automation.enabled ? 'success' : 'neutral'} size="sm">
          {automation.enabled ? 'Ativa' : 'Pausada'}
        </Badge>
      </div>

      <ul className="min-w-0 text-xs text-ink-2 dark:text-ink-dark-2 space-y-1">
        {automation.actions.map((act, idx) => (
          <li key={idx} className="flex flex-wrap items-center gap-1.5 break-words">
            <span>
              {ACTION_LABELS[act.type]} para{' '}
              <strong>{ALVO_LABELS[act.target] ?? act.target}</strong>
            </span>
            {ACTION_EM_PREPARACAO.includes(act.type) && (
              <Badge variant="warning" size="sm">
                Em preparação
              </Badge>
            )}
            {act.message && (
              <span className="text-ink-3 dark:text-ink-dark-3 italic">· {act.message}</span>
            )}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-3 border-t border-line dark:border-line-dark">
        <Button
          size="sm"
          variant="ghost"
          onClick={onDispatch}
          disabled={busy}
          title="Rodar uma vez pra conferir se está configurada do jeito certo"
        >
          Testar
        </Button>
        <Button size="sm" variant="ghost" onClick={onEdit} disabled={busy}>
          Editar
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={onDelete}
          disabled={busy}
          className="sm:ml-auto"
        >
          Excluir
        </Button>
      </div>
      <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-2">
        {automation.totalExecucoes}{' '}
        {automation.totalExecucoes === 1 ? 'execução' : 'execuções'}
        {automation.ultimaExecucaoIso
          ? ` · última em ${formatDataHoraSegundos(automation.ultimaExecucaoIso)}`
          : ''}
      </p>
    </li>
  );
}

